import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { HelpCircle, Sparkles, Loader2, ArrowLeft, Book } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ParentTopicExplainer() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [topic, setTopic] = useState('');
  const [childAge, setChildAge] = useState('8');
  const [explaining, setExplaining] = useState(false);
  const [explanation, setExplanation] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const handleExplain = async () => {
    if (!topic.trim()) {
      toast.error('Please enter a topic');
      return;
    }

    // Check subscription
    if (!user) {
      toast.error('Please sign in to use this tool');
      navigate('/login?redirect=/parent/topic-explainer');
      return;
    }

    // Check if user has parent, plus, or pro subscription
    if (!['parent', 'plus', 'pro'].includes(user.subscription_tier)) {
      toast.error('Please upgrade to access this tool');
      navigate('/parent/pricing');
      return;
    }

    setExplaining(true);
    setExplanation(null);

    try {
      const response = await axios.post(
        `${API}/ai/explain-topic`,
        {
          topic: topic.trim(),
          age: parseInt(childAge)
        },
        { withCredentials: true }
      );

      // Strip markdown symbols for clean display
      const rawExplanation = response.data.explanation || '';
      const cleanExplanation = rawExplanation
        .replace(/\*\*(.+?)\*\*/g, '$1')  // Remove bold **text**
        .replace(/\*(.+?)\*/g, '$1')      // Remove italic *text*
        .replace(/##\s+/g, '')             // Remove ## headings
        .replace(/#\s+/g, '');             // Remove # headings

      setExplanation(cleanExplanation);
      toast.success('Explanation generated!');
    } catch (error) {
      console.error('Error generating explanation:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate explanation');
    } finally {
      setExplaining(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-50">
      <Navigation />
      
      <div className="px-4 py-8 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <button
            onClick={() => navigate('/parent')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Parent Portal
          </button>

          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-2xl mb-4 shadow-lg">
              <HelpCircle className="w-8 h-8 text-white" strokeWidth={2} />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              Topic Explainer
            </h1>
            <p className="text-base text-gray-600">
              Get simple, age-appropriate explanations for any topic
            </p>
          </div>

          {/* Input Form */}
          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 mb-6">
            <div className="space-y-6">
              {/* Topic Input */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  What topic would you like explained?
                </label>
                <textarea
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="E.g., How does photosynthesis work? What is multiplication? Why do we have seasons?"
                  className="w-full rounded-xl border-2 border-gray-200 p-4 text-base focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100 outline-none transition-all resize-none"
                  rows="3"
                />
              </div>

              {/* Age Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Child's Age
                </label>
                <select
                  value={childAge}
                  onChange={(e) => setChildAge(e.target.value)}
                  className="w-full rounded-xl border-2 border-gray-200 p-4 text-base focus:border-indigo-600 focus:ring-2 focus:ring-indigo-100 outline-none transition-all"
                >
                  <option value="5">5 years old</option>
                  <option value="6">6 years old</option>
                  <option value="7">7 years old</option>
                  <option value="8">8 years old</option>
                  <option value="9">9 years old</option>
                  <option value="10">10 years old</option>
                  <option value="11">11 years old</option>
                  <option value="12">12 years old</option>
                </select>
              </div>

              {/* Generate Button */}
              <button
                onClick={handleExplain}
                disabled={explaining}
                className="w-full bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-xl px-6 py-4 font-semibold shadow-md hover:shadow-lg transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {explaining ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Explaining...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Explain This Topic
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Explanation Result */}
          {explanation && (
            <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-200">
                <Book className="w-6 h-6 text-indigo-600" />
                <h2 className="text-xl font-bold text-gray-900">Explanation</h2>
              </div>
              
              <div className="prose max-w-none">
                <div className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {explanation}
                </div>
              </div>

              {/* Tips */}
              <div className="mt-6 bg-blue-50 rounded-xl p-4">
                <p className="text-sm text-blue-900 font-medium mb-1">
                  💡 Tip for Parents:
                </p>
                <p className="text-sm text-blue-700">
                  Use everyday examples and encourage your child to ask questions. Learning is most effective when it's interactive and relatable!
                </p>
              </div>
            </div>
          )}

          {/* Example Topics */}
          {!explanation && (
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                Example Topics:
              </h3>
              <div className="space-y-2">
                {[
                  'How does the water cycle work?',
                  'What is photosynthesis?',
                  'Why do we have day and night?',
                  'What is division?',
                  'How do magnets work?',
                  'What causes seasons?'
                ].map((example, index) => (
                  <button
                    key={index}
                    onClick={() => setTopic(example)}
                    className="w-full text-left px-4 py-3 rounded-lg bg-gray-50 hover:bg-indigo-50 hover:text-indigo-700 transition-colors text-sm"
                  >
                    {example}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default ParentTopicExplainer;
